import { Injectable, Signal, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ModuleKey, EntitlementMode, EntitlementStatus, ModuleAccessMap, FEATURE_ID, METRIC_ID, PARAM_ID, BucketSummary, Project } from '@cosmos/shared';
import { StateService } from './state.service';
import { BillingConfigService } from '@cosmos/angular/api';

@Injectable({
  providedIn: 'root'
})
export class EntitlementService {
  /** Reactive signal of the current module access map */
  readonly moduleAccess = toSignal(this.stateService.currentModuleAccess$, { initialValue: {} as ModuleAccessMap });

  /** Reactive signal of the current project */
  readonly currentProject = toSignal(this.stateService.currentProject$, { initialValue: null as Project | null });

  constructor(
    private stateService: StateService,
    private billingConfigService: BillingConfigService,
  ) {}

  /**
   * Re-fetches the module access map from the backend and pushes it into state.
   * Call this after any action that consumes quota (simulation run, measurement creation, etc.)
   * so that bucket-driven signals (remaining, limitReached) update immediately.
   */
  refreshEntitlements(projectId: string): void {
    this.billingConfigService.getModuleAccess(projectId).subscribe({
      next: (access) => this.stateService.setModuleAccess(access),
    });
  }

  /**
   * Checks if a module is visible in the sidebar/UI.
   */
  isVisible(module: ModuleKey): boolean {
    const accessMap = this.moduleAccess();
    return accessMap?.[module]?.visible ?? false;
  }

  /**
   * Checks if the user has usable access to the module (trial, free or paid).
   * 'preview' access returns false as it usually requires a purchase/activation.
   */
  hasAccess(module: ModuleKey): boolean {
    const accessMap = this.moduleAccess();
    const mode = accessMap?.[module]?.mode;
    return mode === 'paid' || mode === 'free' || mode === 'trial';
  }

  /**
   * Returns the specific commercial mode for a module.
   */
  getMode(module: ModuleKey): EntitlementMode | null {
    const accessMap = this.moduleAccess();
    return accessMap?.[module]?.mode ?? null;
  }

  /**
   * Checks if a module is locked (operational state).
   */
  isLocked(module: ModuleKey): boolean {
    const accessMap = this.moduleAccess();
    return accessMap?.[module]?.status === 'locked';
  }

  /**
   * Checks if a specific feature is enabled for a module.
   * Supports hierarchical checks (e.g. if 'monitoring.create_measurement.maturity'
   * is not explicitly in the list, it checks if 'monitoring.create_measurement' is enabled).
   */
  hasFeature(module: ModuleKey, feature: FEATURE_ID): boolean {
    const accessMap = this.moduleAccess();
    const entry = accessMap?.[module];
    if (!entry) return false;
    if (entry.mode === 'preview') return false;

    // Direct check
    if (entry.features?.includes(feature)) return true;

    // Hierarchical check: an M.K.D feature is allowed if its parent M.K is enabled.
    // Mirrors the backend EntitlementContextService.check() logic so UI gates
    // reflect the same access rules without requiring every sub-feature to be listed.
    const parts = feature.split('.');
    if (parts.length === 3) {
      const parentFeature = `${parts[0]}.${parts[1]}` as FEATURE_ID;
      return entry.features?.includes(parentFeature) ?? false;
    }

    return false;
  }

  /**
   * Like hasFeature but skips the parent-fallback: the feature must be listed explicitly.
   * Use this when unlocking a sub-tier capability (e.g. 3D vs 2D) that must not be
   * inferred from a parent umbrella feature.
   */
  hasExactFeature(module: ModuleKey, feature: FEATURE_ID): boolean {
    const accessMap = this.moduleAccess();
    const entry = accessMap?.[module];
    if (!entry || entry.mode === 'preview') return false;
    return entry.features?.includes(feature) ?? false;
  }

  /** Check if a module is in preview mode (no subscription at all) */
  isPreview(module: ModuleKey): boolean {
    return this.moduleAccess()[module]?.mode === 'preview';
  }

  /** Check if a module has trial access */
  isTrial(module: ModuleKey): boolean {
    return this.moduleAccess()[module]?.mode === 'trial';
  }

  /** Check if a module has free access */
  isFree(module: ModuleKey): boolean {
    return this.moduleAccess()[module]?.mode === 'free';
  }

  /** Returns trial usage / limit for a module (0/undefined when not applicable) */
  trialUsage(module: ModuleKey): { used: number; limit: number | undefined } {
    const entry = this.moduleAccess()[module];
    return { 
      used: entry?.trialConfig?.usage ?? 0, 
      limit: entry?.trialConfig?.limit 
    };
  }

  /**
   * Returns the bucket summary for a specific metric within a module, or null if not present.
   * Use this to read quota usage (remaining / resetTarget) for metered features.
   */
  getBucket(module: ModuleKey, metric: METRIC_ID): BucketSummary | null {
    const entry = this.moduleAccess()[module];
    return entry?.buckets?.find(b => b.metric === metric) ?? null;
  }

  /**
   * Returns the resolved scalar param value (e.g. max_saved_simulations) for the given
   * module's tier, or null if not set. Sourced from the same moduleAccess signal as
   * getBucket, so it shares its fetch/refresh lifecycle — no separate network call needed.
   */
  getParam(module: ModuleKey, param: PARAM_ID): number | null {
    const entry = this.moduleAccess()[module];
    return entry?.params?.[param] ?? null;
  }

  /**
   * Returns a reactive signal indicating whether a feature is currently allowed.
   * Mirrors the backend EntitlementContextService.check() logic:
   *   1. Module must be accessible (not preview, not locked unless allowLocked).
   *   2. Feature must be in the features array (or parent umbrella).
   *   3. If a bucket exists and allowNegative is false, remaining must be > 0.
   *   4. No bucket → pass (backend fallback: allow).
   *
   * Use this to gate any quota-consuming action (submit buttons, form guards).
   */
  isFeatureAllowed(module: ModuleKey, metric: METRIC_ID): Signal<boolean> {
    return computed(() => {
      const entry = this.moduleAccess()[module];

      if (!entry || entry.mode === 'preview' || entry.status === 'locked') {
        return false;
      }

      const feature = metric as unknown as FEATURE_ID;
      if (!this.hasFeature(module, feature)) {
        return false;
      }

      const bucket = entry.buckets?.find(b => b.metric === metric);
      if (!bucket) {
        return true;
      }

      if (bucket.allowNegative) {
        return true;
      }

      return bucket.remaining > 0;
    });
  }

  /** True when at least one module has a usable subscription (non-preview) */
  readonly isProTier = computed(() => {
    const access = this.moduleAccess();
    return Object.values(access).some(a => a.mode !== 'preview');
  });

  /**
   * Derives the combined access state for a module from both project lifecycle
   * and billing state. Checked cheapest-first (project before billing).
   *
   * Returns:
   *   'inactive' — project.active === false (construction finished)
   *   'locked'   — module.status === 'locked' (unpaid / quota exhausted)
   *   'ok'       — no restrictions at module/project level
   *
   * 'preview' and 'disabled' are handled upstream (ModuleAccessGuard and
   * *cosmosModuleAccess) and never reach action elements.
   */
  projectAccessState(module: ModuleKey): Signal<'ok' | 'locked' | 'inactive'> {
    return computed(() => {
      if (this.currentProject()?.active === false) return 'inactive';
      const access = this.moduleAccess()[module];
      if (access?.status === 'locked') return 'locked';
      return 'ok';
    });
  }
}

